import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSettings } from '../../contexts/SettingsContext';

export type CtaTargetKind = 'avatar' | 'community' | 'landingPage' | 'booking' | 'url';

export interface CtaTarget {
  kind: CtaTargetKind;
  value: string;
}

export const emptyCtaTarget = (): CtaTarget => ({ kind: 'avatar', value: '' });

interface Option {
  value: string;
  label: string;
}

interface Props {
  value: CtaTarget;
  onChange: (next: CtaTarget) => void;
  /** Slug da própria LP (não pode ser destino de si mesma). */
  ownSlug: string;
  /** Slug do avatar selecionado, só para o preview do link. */
  avatarSlug: string;
  quizEnabled: boolean;
}

const KIND_LABELS: Record<CtaTargetKind, string> = {
  avatar: 'Página do avatar selecionado',
  community: 'Comunidade',
  landingPage: 'Outra landing page',
  booking: 'Link de agendamento',
  url: 'URL personalizada'
};

const selectCls = 'field field--lg';

/** Destino do botão (CTA) da landing page: colar uma URL ou escolher algo do próprio sistema. */
const CtaTargetPicker: React.FC<Props> = ({ value, onChange, ownSlug, avatarSlug, quizEnabled }) => {
  const { settings } = useSettings();
  const communityEnabled = !!settings?.community?.enabled && settings?.communityConfigured !== false;
  const [options, setOptions] = useState<Option[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<Option[]> => {
      if (value.kind === 'community') {
        const res = await axios.get('/community/admin');
        return (res.data.communities || []).map((c: any) => ({
          value: c.slug,
          label: `${c.name}${c.enabled ? '' : ' (fechada)'}`
        }));
      }
      if (value.kind === 'landingPage') {
        const res = await axios.get('/landing-pages?limit=50');
        return (res.data.landingPages || [])
          .filter((lp: any) => lp.slug && lp.slug !== ownSlug)
          .map((lp: any) => ({
            value: lp.slug,
            label: `${lp.internalName}${lp.status === 'published' ? '' : ' (rascunho)'}`
          }));
      }
      if (value.kind === 'booking') {
        const res = await axios.get('/agendamento/links?status=active&limit=50');
        return (res.data.links || []).map((l: any) => ({ value: l.token, label: l.title }));
      }
      return [];
    };
    if (!['community', 'landingPage', 'booking'].includes(value.kind)) {
      setOptions([]);
      return;
    }
    setLoadingOptions(true);
    load()
      .then((opts) => { if (!cancelled) setOptions(opts); })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoadingOptions(false); });
    return () => { cancelled = true; };
  }, [value.kind, ownSlug]);

  const kinds: CtaTargetKind[] = ['avatar', ...((communityEnabled || value.kind === 'community') ? ['community' as const] : []), 'landingPage', 'booking', 'url'];

  const previewHref = (() => {
    const lp = ownSlug ? `lp=${ownSlug}` : '';
    const withLp = (href: string) => (lp ? `${href}${href.includes('?') ? '&' : '?'}${lp}` : href);
    switch (value.kind) {
      case 'avatar': return avatarSlug ? withLp(`/a/${avatarSlug}`) : '';
      case 'community': return value.value ? withLp(`/comunidade/${value.value}`) : '';
      case 'landingPage': return value.value ? withLp(`/l/${value.value}`) : '';
      case 'booking': return value.value ? withLp(`/agendar/${value.value}`) : '';
      case 'url': return /^https?:\/\//i.test(value.value) ? withLp(value.value) : '';
      default: return '';
    }
  })();

  return (
    <div className="space-y-3">
      <select
        value={value.kind}
        onChange={(e) => onChange({ kind: e.target.value as CtaTargetKind, value: '' })}
        className={`${selectCls} field--select`}
      >
        {kinds.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
      </select>

      {value.kind === 'url' && (
        <input
          type="url"
          value={value.value}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
          placeholder="https://..."
          className={selectCls}
        />
      )}

      {['community', 'landingPage', 'booking'].includes(value.kind) && (
        <select
          value={value.value}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
          className={`${selectCls} field--select`}
          disabled={loadingOptions}
        >
          <option value="">{loadingOptions ? 'Carregando…' : 'Selecione'}</option>
          {value.value && !loadingOptions && !options.some((o) => o.value === value.value) && (
            <option value={value.value}>{value.value} (não encontrado na lista)</option>
          )}
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}

      {!loadingOptions && value.kind !== 'avatar' && value.kind !== 'url' && options.length === 0 && !value.value && (
        <p className="text-xs text-gray-500">Nenhum item disponível para este tipo.</p>
      )}

      {previewHref && (
        <p className="text-xs text-gray-500 break-all">
          Link: <span className="font-mono">{previewHref}</span>
          <br />Os parâmetros de campanha da visita (utm_*, fbclid, gclid) também são repassados.
        </p>
      )}

      {quizEnabled && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
          O quiz está ativo: o botão abre o quiz. Este destino só vale com o quiz desligado.
        </p>
      )}
    </div>
  );
};

export default CtaTargetPicker;
